/**
 * React hooks for extension management.
 */

import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SalvageYardID } from "../../constants/FamaExtension";
import { useAppContext } from "../../AppProviders";
import {
  ExtensionConnectionStatus,
  ExtensionStatus,
  ReleaseChannel,
  ReleaseInfo,
  UpdateCheckResult,
  UpdateStatus,
} from "./types";
import { isVersionLessThan } from "./versionUtils";
import { fetchReleaseInfo } from "./api";
import { getChromeApi } from "./chrome";

/**
 * Ping the extension to check if it's installed and get its version.
 * Uses a timeout to prevent hanging if extension is unresponsive.
 */
async function pingExtension(
  timeoutMs: number = 3000
): Promise<{ ok: boolean; version?: string }> {
  const chromeApi = getChromeApi();
  if (!chromeApi) {
    console.debug("[Extension] Chrome API not available");
    return { ok: false };
  }

  console.debug("[Extension] Pinging extension...", {
    extensionId: SalvageYardID,
    timeoutMs,
  });

  return new Promise((resolve) => {
    let resolved = false;

    // Timeout fallback - extension might be installed but unresponsive
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.debug("[Extension] Ping timed out after", timeoutMs, "ms");
        resolve({ ok: false });
      }
    }, timeoutMs);

    try {
      chromeApi.runtime.sendMessage(
        SalvageYardID,
        { action: "PING" },
        (response: { ok?: boolean; version?: string } | undefined) => {
          if (resolved) return; // Already timed out
          resolved = true;
          clearTimeout(timeoutId);

          if (chromeApi.runtime.lastError) {
            console.debug(
              "[Extension] Ping failed:",
              chromeApi.runtime.lastError.message
            );
            resolve({ ok: false });
            return;
          }
          if (response?.ok && response?.version) {
            console.info("[Extension] Ping successful", {
              version: response.version,
            });
            resolve({ ok: true, version: response.version });
          } else {
            console.debug("[Extension] Ping response invalid:", response);
            resolve({ ok: false });
          }
        }
      );
    } catch (e) {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeoutId);
        console.warn("[Extension] Ping exception:", e);
        resolve({ ok: false });
      }
    }
  });
}

/**
 * Retry ping with exponential backoff.
 * Useful after extension install when it might take a moment to initialize.
 */
async function pingWithRetry(
  maxAttempts: number = 3,
  initialDelayMs: number = 500
): Promise<{ ok: boolean; version?: string }> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await pingExtension(2000);
    if (result.ok) {
      return result;
    }

    if (attempt < maxAttempts - 1) {
      const delay = initialDelayMs * Math.pow(2, attempt);
      console.debug("[Extension] Ping retry", {
        attempt: attempt + 1,
        nextDelay: delay,
      });
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  return { ok: false };
}

/**
 * Determine update status by comparing versions.
 *
 * Policy:
 * - Enforces required updates based on min_required_version
 * - Optional updates for newer versions
 */
function determineUpdateStatus(
  currentVersion: string,
  releaseInfo: ReleaseInfo
): UpdateStatus {
  const { latest_version, min_required_version } = releaseInfo;

  console.debug("[Extension] Comparing versions", {
    currentVersion,
    latestVersion: latest_version,
    minRequiredVersion: min_required_version,
  });

  // Enforce required updates based on min_required_version
  if (
    min_required_version &&
    isVersionLessThan(currentVersion, min_required_version)
  ) {
    console.info("[Extension] Required update detected", {
      currentVersion,
      minRequiredVersion: min_required_version,
    });
    return "required";
  }

  // Optional updates for newer versions
  if (isVersionLessThan(currentVersion, latest_version)) {
    console.info("[Extension] Optional update available", {
      currentVersion,
      latestVersion: latest_version,
    });
    return "optional";
  }

  // Up to date
  console.debug("[Extension] Extension is up to date", { currentVersion });
  return "none";
}

/**
 * Hook to get extension connection status and version.
 * Pings the extension on mount.
 */
export function useExtensionStatus(): ExtensionStatus {
  const { requestApi } = useAppContext();
  const [connectionStatus, setConnectionStatus] =
    useState<ExtensionConnectionStatus>("unknown");
  const [version, setVersion] = useState<string | null>(null);
  const [updateInfo, setUpdateInfo] = useState<UpdateCheckResult | null>(null);

  // Always use prod channel for release info
  const channel: ReleaseChannel = "prod";

  // Fetch release info:
  // - When connected: fetch prod release info
  // - When not installed: fetch prod for first-time install download
  // - When connection_failed: Chrome API unavailable, still need download URL
  const shouldFetchReleaseInfo =
    connectionStatus === "connected" ||
    connectionStatus === "not_installed" ||
    connectionStatus === "connection_failed";

  const { data: releaseInfo, refetch: refetchReleaseInfo } = useQuery<
    ReleaseInfo,
    Error
  >(["extension-release-info"], () => fetchReleaseInfo(requestApi), {
    enabled: shouldFetchReleaseInfo,
    staleTime: 0, // Always fetch fresh - S3 is cheap, we want latest
    cacheTime: 0, // Don't cache - always get latest from S3
    retry: 2,
  });

  // Function to check extension status - can be called manually to recheck
  // Also refetches release info to get latest version from S3
  // useRetry: if true, uses retry mechanism (good for after install/update)
  const checkExtension = useCallback(
    async (useRetry: boolean = false) => {
      console.info("[Extension] Checking extension status...", { useRetry });

      // Also refetch release info to get latest from S3
      refetchReleaseInfo();

      // Use retry mechanism if requested (e.g., after "Check Again" click)
      const result = useRetry
        ? await pingWithRetry(3, 500)
        : await pingExtension();

      if (result.ok && result.version) {
        console.info("[Extension] Extension connected", {
          version: result.version,
        });
        setConnectionStatus("connected");
        setVersion(result.version);
        return true;
      } else {
        // Could be not installed or connection failed
        // Chrome API being unavailable means probably not in Chrome
        const chromeApi = getChromeApi();
        if (!chromeApi) {
          console.warn(
            "[Extension] Chrome API unavailable - not in Chrome browser"
          );
          setConnectionStatus("connection_failed");
        } else {
          console.info("[Extension] Extension not installed");
          setConnectionStatus("not_installed");
        }
        return false;
      }
    },
    [refetchReleaseInfo]
  );

  // Aggressive check with retries - for use after user installs extension
  const checkExtensionWithRetry = useCallback(() => {
    return checkExtension(true);
  }, [checkExtension]);

  // Ping extension on mount
  useEffect(() => {
    let mounted = true;

    checkExtension().then(() => {
      if (!mounted) return;
    });

    return () => {
      mounted = false;
    };
  }, [checkExtension]);

  // Re-check when page becomes visible (user might have just installed extension)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Only re-check if not already connected
        if (connectionStatus !== "connected") {
          console.info(
            "[Extension] Page became visible, re-checking extension..."
          );
          checkExtension();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [connectionStatus, checkExtension]);

  // Compute update status when we have release info
  // For not_installed/connection_failed: provide download URL for first-time install (always prod)
  // For connected: compare versions to determine update status
  useEffect(() => {
    if (!releaseInfo) {
      console.debug("[Extension] No release info available yet");
      setUpdateInfo(null);
      return;
    }

    // Not installed or connection_failed - provide download URL for first-time install
    if (
      connectionStatus === "not_installed" ||
      connectionStatus === "connection_failed"
    ) {
      console.info("[Extension] Providing first-time install info", {
        connectionStatus,
        latestVersion: releaseInfo.latest_version,
        hasDownloadUrl: !!releaseInfo.download_url,
      });
      setUpdateInfo({
        status: "none", // No "update" needed, just install
        currentVersion: null,
        latestVersion: releaseInfo.latest_version,
        downloadUrl: releaseInfo.download_url,
        channel: "prod",
      });
      return;
    }

    // Connected - compare versions
    if (!version) {
      console.debug("[Extension] Connected but no version available");
      setUpdateInfo(null);
      return;
    }

    const status = determineUpdateStatus(version, releaseInfo);
    console.info("[Extension] Update status computed", {
      status,
      currentVersion: version,
      latestVersion: releaseInfo.latest_version,
    });
    setUpdateInfo({
      status,
      currentVersion: version,
      latestVersion: releaseInfo.latest_version,
      downloadUrl: releaseInfo.download_url,
      channel: "prod",
    });
  }, [version, releaseInfo, connectionStatus]);

  // Derive update status from update info
  const updateStatus: UpdateStatus = updateInfo?.status || "none";

  return {
    connectionStatus,
    version,
    channel,
    updateStatus,
    updateInfo,
    recheckExtension: checkExtension,
    recheckExtensionWithRetry: checkExtensionWithRetry,
  };
}
